import type * as presetsApi from "@pax/api-contract/presets/v1alpha1";
import { reduceWorld } from "@pax/play";
import { parseWorldArtifact } from "@pax/world";
import {
  type InfiniteData,
  infiniteQueryOptions,
  type QueryClient,
  queryOptions,
} from "@tanstack/react-query";
import { type Session, sessionKey } from "@/features/auth/queries";
import { publicApi, publicOrpc, reachable, revalidatedPublicApi } from "@/lib/api";

const PRESET_PAGE_SIZE = 20;
const MAX_PRESET_SEARCH_LENGTH = 100;

export function normalizePresetSearch(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const query = value.trim().toLowerCase().slice(0, MAX_PRESET_SEARCH_LENGTH);
  return query || undefined;
}

export const presetListingsQuery = (q?: string) => {
  const normalizedQuery = normalizePresetSearch(q);

  return infiniteQueryOptions({
    queryKey: [...publicOrpc.presets.list.key(), { q: normalizedQuery ?? null }],
    queryFn: ({ pageParam }) =>
      reachable(
        publicApi.presets.list({
          query: {
            limit: PRESET_PAGE_SIZE,
            ...(normalizedQuery ? { q: normalizedQuery } : {}),
            ...(pageParam ? { cursor: pageParam } : {}),
          },
        }),
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage: presetsApi.ListPresetsResponse) => lastPage.nextCursor,
    staleTime: 60_000,
  });
};

/**
 * Signed in, the same read also says what the reader may do with the preset, so the browser's
 * shared copy only serves a signed-out read that nothing has invalidated. Invalidation follows a
 * sign-in or sign-out, which can land before the new session is cached.
 */
function presetQuery<Selected>(
  slug: string,
  select: (response: presetsApi.GetPresetResponse) => Selected,
) {
  return queryOptions({
    queryKey: publicOrpc.presets.get.queryKey({ input: { params: { slug } } }),
    // No abort signal: a fetch that takes one is cancelled when the last page showing the preset
    // unmounts, and the next route's loader, which joined that fetch, fails with it. Starting a game
    // from the scenario page does exactly that.
    queryFn: ({ client, queryKey }) => {
      const signedIn = Boolean(client.getQueryData<Session | null>(sessionKey));
      const invalidated = client.getQueryState(queryKey)?.isInvalidated === true;
      return (signedIn || invalidated ? revalidatedPublicApi : publicApi).presets.get({
        params: { slug },
      });
    },
    select,
    staleTime: 60_000,
  });
}

export const presetDetailQuery = (slug: string) => presetQuery(slug, response => response.preset);

export const presetViewerQuery = (slug: string) => presetQuery(slug, response => response.viewer);

export const presetVersionQuery = (versionId: string) =>
  publicOrpc.presets.versions.get.queryOptions({
    input: { params: { versionId } },
    select: response => response.preset,
    staleTime: Number.POSITIVE_INFINITY,
  });

/**
 * What is already known about a scenario before its own request has answered.
 *
 * Arriving from a rail means the browser holds the cover and landing poster,
 * and the cache holds the title, author and tint track — everything above the
 * fold except the world. So the screen opens looking like itself and fills in
 * underneath, rather than opening as a grey rectangle that later becomes a
 * scenario. Undefined on a cold deep link, which is the one case that has
 * nothing to go on.
 */
export function cachedPresetListing(
  queryClient: QueryClient,
  slug: string,
): presetsApi.PresetListing | undefined {
  const cached = queryClient.getQueriesData<InfiniteData<presetsApi.ListPresetsResponse>>({
    queryKey: publicOrpc.presets.list.key(),
  });
  for (const [, data] of cached) {
    for (const page of data?.pages ?? []) {
      const listing = page.items.find(item => item.slug === slug);
      if (listing) return listing;
    }
  }
  return undefined;
}

/**
 * A scenario's world, read by the map worker so that neither the megabytes of
 * text nor the polygons inside them ever cross this thread. What the page
 * holds is the world without its outlines — every region with its name, owner
 * and centroid, and an empty geometry — which is everything a page needs to
 * describe the world and nothing it needs to draw it. The map is drawn from
 * the worker's copy, asked for by the same URL.
 */
export const presetWorldQuery = (versionId: string, url: string) =>
  queryOptions({
    queryKey: ["preset-versions", versionId, "world"],
    queryFn: async () => {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`The world did not load (${response.status}).`);
      return reduceWorld(parseWorldArtifact(await response.text())).spectator;
    },
    staleTime: Number.POSITIVE_INFINITY,
  });
